import { inject, Service } from '@angular/core';
import type {
  OnyxComponentInputs,
  OnyxDropdownOptionsSourceResult,
  OnyxOption,
  OnyxPaginated,
  OnyxPagination,
} from '@onyx/angular';
import { OnyxDatePipe, OnyxNamePipe } from '@onyx/angular';
import { omit } from 'lodash';
import type { Observable } from 'rxjs';
import { map, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { AuthStore } from '../../../../../auth/common/stores/auth.store';
import { IconTextTooltipComponent } from '../../../../../common/components/tooltips/icon-text-tooltip/icon-text-tooltip.component';
import type { UnavailabilityForm } from '../../../../../common/components/unavailabilities/unavailability-modal/unavailability-modal.component';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import type { Unavailability } from '../../../../../common/interfaces/common/unavailability';
import type { BatchFileUpload } from '../../../../../common/interfaces/utilities/batch-file-upload';
import { JoinPipe } from '../../../../../common/pipes/join.pipe';
import { ApiService } from '../../../../../common/services/api.service';
import { StorageService } from '../../../../../common/services/storage.service';
import type { SimplifiedFleet } from '../../../../fleet/common/interfaces/fleet';
import { FleetService } from '../../../../fleet/common/services/fleet.service';
import type { AddEmployeesModalForm } from '../../add-employees-modal/add-employees-modal.component';
import type { EditEmployeeModalForm } from '../components/edit-employee-modal/edit-employee-modal.component';
import { EmployeeCategory } from '../enums/employee-category';
import type { EmployeeRole } from '../enums/employee-role';
import type { EmployeeStatus } from '../enums/employee-status';
import type { Employee } from '../interfaces/employee';

export interface EmployeesFilters {
  category: EmployeeCategory;
  roles: EmployeeRole[];
  statuses: EmployeeStatus[];
  query?: string;
}

@Service()
export class EmployeesService extends ApiService {
  private readonly authStore = inject(AuthStore);
  private readonly storageService = inject(StorageService);
  private readonly joinPipe = inject(JoinPipe);
  private readonly datePipe = inject(OnyxDatePipe);
  private readonly namePipe = inject(OnyxNamePipe);
  private readonly fleetService = inject(FleetService);

  private _reload$ = new Subject<void>();
  public get reload$() {
    return this._reload$.asObservable();
  }

  public listEmployees(
    params: OnyxPagination & EmployeesFilters,
  ): Observable<OnyxPaginated<Employee>> {
    return this.get('/employees', {
      params: {
        page: params.page,
        limit: params.limit,
        category: params.category,
        'roles[]': params.roles,
        ...(params.category === EmployeeCategory.EMPLOYED && {
          'status[]': params.statuses,
        }),
        ...(params.query && { query: params.query }),
      },
    });
  }

  public searchEmployees(
    query: string,
    params: Omit<OnyxPagination & EmployeesFilters, 'query'>,
  ): Observable<OnyxDropdownOptionsSourceResult<Employee>> {
    return this.reload$.pipe(
      startWith(undefined),
      switchMap(() => this.listEmployees({ ...params, query })),
      map((response) => ({
        options: response.items.map((employee): OnyxOption<Employee> => ({
          name: this.namePipe.transform(employee),
          value: employee,
          avatar: employee,
          description: this.joinPipe.transform(
            employee.roles.map((role) => role.value),
            {
              separator: ' · ',
              maxLength: 2,
              i18nKey: DictionaryCode.EMPLOYEE_ROLE,
            },
          ),
          ...(employee.unavailability && {
            rightIcon: {
              name: `${employee.unavailability.reason}-outlined`,
              size: 20,
            },
            rightIconTooltip: {
              ref: IconTextTooltipComponent,
              inputs: {
                icon: {
                  name: `${employee.unavailability.reason}-outlined`,
                  size: 20,
                },
                heading: `${DictionaryCode.EMPLOYEE_UNAVAILABILITY_REASON}.${employee.unavailability.reason}`,
                description: this.datePipe.transform(
                  employee.unavailability.dateRange.from,
                  'date-dot',
                  { endDate: employee.unavailability.dateRange.to },
                ),
              } satisfies OnyxComponentInputs<IconTextTooltipComponent>,
            },
          }),
        })),
        totalItems: response.totalItems,
      })),
    );
  }

  public getEmployee(uuid: string): Observable<Employee> {
    return this.get<Employee>(`/employees/${uuid}`);
  }

  public addEmployees(form: AddEmployeesModalForm): Observable<void> {
    return this.post<void>('/employees', form).pipe(
      tap(() => this._reload$.next()),
    );
  }

  public editEmployee(
    uuid: string,
    form: EditEmployeeModalForm,
  ): Observable<Employee> {
    const body = omit(form, 'email');
    return this.put<Employee>(`/employees/${uuid}`, body).pipe(
      tap(() => {
        const user = this.authStore.user();
        if (uuid !== user?.employeeUuid) return;

        this.authStore.setRoles(form.roles!);
        this.authStore.updateUser({
          firstName: form.firstName,
          lastName: form.lastName,
          phone: form.phone!,
          roles: form.roles!,
        });
      }),
      tap(() => this._reload$.next()),
    );
  }

  public getEmployeeUnavailabilities(
    employeeUuid: string,
    params: OnyxPagination & { past: boolean },
  ): Observable<OnyxPaginated<Unavailability>> {
    return this.get(`/employees/${employeeUuid}/unavailabilities`, {
      params: {
        page: params.page,
        limit: params.limit,
        past: params.past,
      },
    });
  }

  public addEmployeeUnavailability(
    employeeUuid: string,
    form: UnavailabilityForm,
  ): Observable<void> {
    return this.uploadUnavailabilityFile(form).pipe(
      switchMap((form) =>
        this.post<void>(`/employees/${employeeUuid}/unavailabilities`, form),
      ),
      tap(() => this.reloadRelated()),
    );
  }

  public editEmployeeUnavailability(
    employeeUuid: string,
    uuid: string,
    form: UnavailabilityForm,
  ): Observable<void> {
    return this.uploadUnavailabilityFile(form).pipe(
      switchMap((form) =>
        this.put<void>(
          `/employees/${employeeUuid}/unavailabilities/${uuid}`,
          form,
        ),
      ),
      tap(() => this.reloadRelated()),
    );
  }

  public deleteEmployeeUnavailability(
    employeeUuid: string,
    uuid: string,
  ): Observable<void> {
    return this.delete<void>(
      `/employees/${employeeUuid}/unavailabilities/${uuid}`,
    ).pipe(tap(() => this.reloadRelated()));
  }

  public reinviteEmployee(uuid: string): Observable<void> {
    return this.post<void>(`/employees/${uuid}/resend-invite`, undefined).pipe(
      tap(() => this._reload$.next()),
    );
  }

  public getInviteLink(uuid: string): Observable<string> {
    return this.get<{ inviteLink: string }>(
      `/employees/${uuid}/invite-link`,
    ).pipe(map(({ inviteLink }) => inviteLink));
  }

  public unarchiveEmployee(uuid: string): Observable<void> {
    return this.post<void>(`/employees/${uuid}/unarchive`, undefined).pipe(
      tap(() => this.reloadRelated()),
    );
  }

  public archiveEmployee(uuid: string): Observable<void> {
    return this.post<void>(`/employees/${uuid}/archive`, undefined).pipe(
      tap(() => this.reloadRelated()),
    );
  }

  public deleteEmployee(uuid: string): Observable<void> {
    return this.delete<void>(`/employees/${uuid}`).pipe(
      tap(() => this.reloadRelated()),
    );
  }

  public getAssignedVehicles(
    uuid: string,
    pagination: OnyxPagination,
  ): Observable<OnyxPaginated<SimplifiedFleet>> {
    return this.get(`/employees/${uuid}/assigned-vehicles`, {
      params: {
        page: pagination.page,
        limit: pagination.limit,
      },
    });
  }

  public assignVehicles(uuid: string, vehicles: string[]): Observable<void> {
    return this.put<void>(`/employees/${uuid}/assigned-vehicles`, {
      vehicles,
    }).pipe(tap(() => this.reloadRelated()));
  }

  private uploadUnavailabilityFile(
    form: UnavailabilityForm,
  ): Observable<UnavailabilityForm> {
    return of(form).pipe(
      map((form): BatchFileUpload[] => [
        {
          path: 'file',
          files: form.file,
        },
      ]),
      switchMap((data) => this.storageService.uploadBatch(data)),
      map((data) => this.storageService.mergeBatch(form, data)),
    );
  }

  private reloadRelated(): void {
    this._reload$.next();
    this.fleetService['_reload$'].next();
  }
}
